import { useCallback } from 'react';
import { useSession } from '@/features/auth/use-session';
import type { PermissionScope } from '@/lib/types';

/**
 * Permission checks for the UI. These hide controls the user cannot use — the
 * server re-checks every one of them, so this is convenience, never security.
 */
export function usePermission() {
  const { data: user } = useSession();
  const permissions = user?.permissions ?? {};

  const can = useCallback(
    (permission: string): boolean => permission in permissions,
    [permissions],
  );

  const canAny = useCallback(
    (...keys: string[]): boolean => keys.some((key) => key in permissions),
    [permissions],
  );

  const canAll = useCallback(
    (...keys: string[]): boolean => keys.every((key) => key in permissions),
    [permissions],
  );

  const scopeOf = useCallback(
    (permission: string): PermissionScope | undefined => permissions[permission],
    [permissions],
  );

  return { can, canAny, canAll, scopeOf, permissions };
}
